import { useSelector } from 'react-redux';

import Button from '@components/button/Button';
import Icon from '@components/icon/Icon';
import { MetaIntegrationProvider } from '@features/lead-management/components/lead-settings/types';
import { MetaSyncItem } from '@features/lead-management/metaSyncSlice';
import { LeadImportOption } from '@features/lead-management/types/common';
import { META_SYNC_STATUS } from '@features/lead-management/utils/metaIntegrationHelpers';

import './LeadListEmptyView.css';

interface LeadListEmptyViewProps {
  onCsvUpload?: () => void;
  onAddManually?: () => void;
  showMetaOption?: boolean;
  onSyncFromMeta?: () => void;
  metaProvider?: MetaIntegrationProvider | null;
}

const LeadListEmptyView = ({
  onCsvUpload,
  onAddManually,
  showMetaOption = false,
  onSyncFromMeta,
  metaProvider
}: LeadListEmptyViewProps) => {
  const syncs = useSelector(
    (state: any) => state.metaSync?.items || []
  ) as MetaSyncItem[];

  const isMetaSyncing = syncs.some(
    (sync) => sync.status === META_SYNC_STATUS.SYNCING
  );

  const getMetaButtonLabel = () => {
    if (isMetaSyncing) return 'Syncing...';
    return metaProvider ? 'Sync' : 'Connect';
  };

  const leadImportOptions: LeadImportOption[] = [
    {
      id: 'csv-upload',
      iconName: 'doc-upload',
      iconClassName: 'h-25.5 w-29',
      title: 'CSV/Excel Upload',
      description: 'Upload leads from spreadsheet files',
      buttonLabel: 'Start Import',
      onClick: onCsvUpload
    },
    {
      id: 'manual-add',
      iconName: 'manual-add',
      iconClassName: 'size-25.5',
      title: 'Add manually',
      description: 'Add individual leads manually',
      buttonLabel: 'Add Manually',
      onClick: onAddManually
    },
    ...(showMetaOption
      ? [
          {
            id: 'lead-via-meta',
            iconName: 'lead-via-meta',
            iconClassName: 'h-25.5 w-29',
            title: 'Sync from Meta',
            description: 'Automatically import leads from Meta forms',
            buttonLabel: getMetaButtonLabel(),
            onClick: onSyncFromMeta,
            disabled: isMetaSyncing || metaProvider?.is_active === false,
            buttonClassName: 'w-fit min-w-[136px]',
            disabledTooltip:
              metaProvider?.is_active === false
                ? 'Lead sync is disabled in Settings. Turn it on to sync now'
                : undefined
          }
        ]
      : [])
  ];

  return (
    <div className="flex flex-col items-center justify-center p-8">
      <div className="flex flex-col items-center gap-6 rounded-2xl bg-white p-8 shadow-lg">
        <div className="flex flex-col items-center gap-1">
          <div className="text-center text-2xl font-medium text-content">
            How would you like to import leads?
          </div>
          <div className="mt-1 text-center text-lg font-normal text-grey">
            Choose an import method to begin adding leads
          </div>
        </div>

        <div className="flex items-center gap-6">
          {leadImportOptions.map((option) => (
            <div
              key={option.id}
              className="flex min-w-80 flex-1 flex-col items-center justify-center gap-6 rounded-xl border border-grey30 bg-white p-8"
            >
              <div
                className={`flex items-center justify-center ${option.iconClassName}`}
              >
                <Icon
                  name={option.iconName}
                  size="none"
                  className={option.iconClassName}
                />
              </div>
              <div className="flex flex-col items-center gap-3">
                <div className="text-center text-xl font-semibold text-content">
                  {option.title}
                </div>
                <div className="whitespace-nowrap text-center text-base font-normal text-grey">
                  {option.description}
                </div>
              </div>
              <Button
                variant="secondary"
                label={option.buttonLabel}
                handleClick={option.onClick}
                className={option.buttonClassName ?? 'w-fit'}
                disabled={option.disabled}
                tooltipText={option.disabledTooltip}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default LeadListEmptyView;
